import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kEditableColumn, kEditableColumns, kEditableTable, kEditableTableCell, kEditableTableRow, kHTMLEditableColType } from "../../../Admin/ReUsableTS/kEditableTable";
import { kObject, kObjectArray } from "../../../Admin/ReUsableTS/kBrowserObjects";
import { kStyles } from "../../../Admin/ReUsableTS/kStyles";
import { kExcelExport } from "../../../Admin/ReUsableTS/kExcelExport";
import { AppMoneyColumn, AppMoneyEntry, AppMoneyModel } from "./AppMoneyModel";

// AppMoneyTable - THE HOST of the Revenue items tables: the ONE place that creates the shared `kEditableTable`
// (`Admin/ReUsableTS`, decoupled from kalmstromShared4 on 2026-10-14 - its `ks` delegates to whatever handler the HOST
// registered, here `AppErrorInstall`'s `AppErrorReporter`, installed first by `AppMain`).
// ⛔ CSP: `kStyles.UseAdoptedSheets = true` BEFORE the first table renders - the grid's stylesheet then goes in as a
// constructed sheet (CSSOM, not governed by `style-src`) instead of a `<style>` element the app's policy refuses.
// The columns are `AppMoneyModel.Columns()` (as `IkColumnSpec`s), one row per `AppMoneyModel.Cells(E)`; an edited
// cell reaches `OnEdit(Id, Column, Value)` = `AppMoneyRun.OnCellEdit`, which turns it into ONE `/money` action
// (`AppMoneyModel.EditOf`). A column outside `AppMoneyModel.Editable` is put back on an existing item.
// ⭐ NEW ITEMS: the grid's own trailing blank row (`AddBlankRows`, one table per view - the host marked `AddAttr`).
// Leaving a filled add-row (Enter, Tab off its last cell, a click elsewhere, focus leaving the table) hands its
// values to `IAppMoneyAdder.AddRow` ONCE per leave, with the row's page-made `v-` id kept for a retry.
// ⭐ Each item's row carries its own "open the message" link (`Open` column, a `CustomRenderer` `<a>` - the grid lets a
// real link navigate and answers Enter on such a cell by clicking it). ⛔ Never imported by the pure files (DOM).

export type AppMoneyEdited = (Id: string, Column: string, Value: string) => void;

/** The run's side of the add-row: send `Cells` as a new item with `Id`; answers the columns to mark (none = sent). */
export interface IAppMoneyAdder {
    AddRow(Id: string, Cells: Record<string, string>): string[];
    NewId(): string;
}

export class AppMoneyTable {
    /** One live grid per host, so a repaint drops the old one. */
    private static Live: kEditableTable[] = [];

    /** Fill `Host` with a `kEditableTable` of `Rows`; `Adder` non-null = this grid carries the add-row. */
    public static Mount = (Host: HTMLElement, Rows: AppMoneyEntry[], OnEdit: AppMoneyEdited, Adder: IAppMoneyAdder | null, Contacts: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            kStyles.UseAdoptedSheets = true;
            const Grid: kEditableTable = new kEditableTable();
            Grid.AddBlankRows = Adder !== null;
            if (Adder !== null) { Grid.AddRowHint = "New item"; }
            Grid.Columns = AppMoneyTable.ColumnsOf(AppMoneyModel.Columns(Contacts), new AppMoneyTableLink(Rows).Paint);
            Grid.RowValues = AppMoneyTable.RowsOf(Rows);
            Grid.CellDirtiedCB = new AppMoneyTableEdit(Grid, Rows, OnEdit).OnDirty;
            if (Adder !== null) { AppMoneyTable.Listen(Host, new AppMoneyTableAdd(Grid, Host, Rows.length, Adder)); }
            Host.textContent = "";
            Host.appendChild(Grid.ToTable());
            Grid.Table.setAttribute("aria-label", nz(Host.getAttribute("aria-label")));
            AppMoneyTable.MarkFixed(Grid, Rows.length);
            AppMoneyTable.MarkWidths(Grid);
            AppMoneyTable.Live.push(Grid);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.Mount");
        }
    };

    /**
     * Every grid now on the screen as one sheet each of ONE workbook (shared `kExcelExport.ExportTables`), named by its
     * table's label ("Owed to you", "You owe" ...). Grids from an earlier paint are dropped from `Live` here.
     */
    public static ExportAll = (FileName: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppMoneyTable.Live = AppMoneyTable.Live.filter((G: kEditableTable): boolean => G.Table !== null && G.Table !== undefined && G.Table.isConnected === true);
            if (AppMoneyTable.Live.length === 0) { return; }
            const Names: string[] = [];
            for (const G of AppMoneyTable.Live) { Names.push(nz(G.Table.getAttribute("aria-label"))); }
            new kExcelExport().ExportTables(AppMoneyTable.Live, Names, FileName);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.ExportAll");
        }
    };

    private static Listen = (Host: HTMLElement, Add: AppMoneyTableAdd): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Add.Grid.CellSelectionChangedCB = Add.OnSelect;
            document.addEventListener("mousedown", Add.OnLeave, true);
            Host.addEventListener("mousedown", Add.OnTouch);
            Host.addEventListener("focusin", Add.OnTouch);
            Host.addEventListener("click", Add.OnTouch);
            Host.addEventListener("keyup", Add.OnTouch);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.Listen");
        }
    };

    /**
     * The header row decides a fixed layout's widths: the grid's own row-number cell gets class `mrow` (narrow) and the
     * `AppMoneyModel.SlackColumn` header `mslack` (its declared width is a floor; it takes whatever room is left).
     */
    private static MarkWidths = (Grid: kEditableTable): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // The header cells are the grid's `Headers` (one per column); the row-number heading stands just before the first.
            if (Grid.Headers.length === 0 || Grid.Headers[0].HeaderCell === null) { return; }
            const Corner: Element | null = (Grid.Headers[0].HeaderCell as HTMLTableCellElement).previousElementSibling;
            if (Corner !== null) { Corner.classList.add("mrow"); }
            for (let I: number = 0; I < Grid.Headers.length && I < Grid.Columns.Items.length; I++) {
                const Th: HTMLTableCellElement | null = Grid.Headers[I].HeaderCell;
                if (Th !== null && Grid.Columns.Items[I].KObjectPropertyName === AppMoneyModel.SlackColumn) { Th.classList.add("mslack"); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.MarkWidths");
        }
    };

    /** An existing item's Contact / For / Since cells read as fixed (they are filled only on the add-row). */
    private static MarkFixed = (Grid: kEditableTable, Count: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (let I: number = 0; I < Count && I < Grid.Rows.Items.length; I++) {
                for (const Cell of Grid.Rows.Items[I].Cells) {
                    const Col: string = nz(Cell.ColDef.KObjectPropertyName);
                    // The description may end in an ellipsis in a narrow pane: the cell's tooltip gives the whole of it.
                    if (Col === AppMoneyModel.SlackColumn) { Cell.Cell.title = nz(Cell.Cell.textContent).trim(); }
                    if (AppMoneyModel.Editable.indexOf(Col) >= 0 || Cell.ColDef.Locked === true) { continue; }
                    Cell.Cell.classList.add("mfixed");
                    Cell.Cell.setAttribute("aria-readonly", "true");
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.MarkFixed");
        }
    };

    /** The model's specs as the grid's columns (`kEditableColumns.FromSchema`); the link column gets `Paint`. */
    public static ColumnsOf = (Specs: AppMoneyColumn[], Paint: ((Value: unknown, Cell: kEditableTableCell) => void) | null): kEditableColumns => {
        const Out: kEditableColumns = new kEditableColumns();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            const Mapped: { InternalName: string; Header: string; Type: kHTMLEditableColType; Options: string[]; Locked: boolean; Width: number }[] = [];
            for (const C of Specs) {
                let Type: kHTMLEditableColType = kHTMLEditableColType.Text;
                if (C.Type === "select") { Type = kHTMLEditableColType.Select; }
                Mapped.push({ InternalName: C.InternalName, Header: C.Header, Type: Type, Options: C.Options, Locked: C.Locked, Width: C.Width });
            }
            for (const Col of kEditableColumns.FromSchema(Mapped, false)) {
                const One: kEditableColumn = Col as kEditableColumn;
                if (One.KObjectPropertyName === AppMoneyModel.OpenColumn) { One.CustomRenderer = Paint; }
                if (One.Type === kHTMLEditableColType.Select) { One.AllowAddNewItem = false; }
                Out.push(One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.ColumnsOf");
            return Out;
        }
    };

    /** One bound object per entry, its properties named as the columns' `InternalName`s. */
    public static RowsOf = (Rows: AppMoneyEntry[]): kObjectArray => {
        const Out: kObjectArray = new kObjectArray();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            for (const E of Rows) {
                const One: kObject = new kObject();
                const Bag: Record<string, unknown> = One as unknown as Record<string, unknown>;
                const Cells: Record<string, string> = AppMoneyModel.Cells(E);
                for (const Key of Object.keys(Cells)) { Bag[Key] = Cells[Key]; }
                Out.push(One);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTable.RowsOf");
            return Out;
        }
    };
}

/** One grid's edit callback: the dirtied cell of an existing item -> (item id, column, value). */
export class AppMoneyTableEdit {
    private Grid: kEditableTable;
    private Rows: AppMoneyEntry[];
    private OnEdit: AppMoneyEdited;

    public constructor(Grid: kEditableTable, Rows: AppMoneyEntry[], OnEdit: AppMoneyEdited) {
        this.Grid = Grid;
        this.Rows = Rows;
        this.OnEdit = OnEdit;
    }

    public OnDirty = (Cell: kEditableTableCell | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Cell === null || Cell === undefined) { return; }
            const E: AppMoneyEntry | undefined = this.Rows[Cell.RowIndex];
            if (E === undefined) { return; }
            const Col: string = nz(Cell.ColDef.KObjectPropertyName);
            let Value: string = "";
            if (Cell.CurrentValue !== null && Cell.CurrentValue !== undefined) { Value = String(Cell.CurrentValue); }
            const Was: string = nz(AppMoneyModel.Cells(E)[Col]);
            if (Value === Was) { return; }
            if (AppMoneyModel.Editable.indexOf(Col) < 0) {
                const Row: kEditableTableRow | undefined = this.Grid.Rows.Items[Cell.RowIndex];
                if (Row !== undefined) { Row.SetValueToCell(Col, Was, false); }
            }
            this.OnEdit(E.Id, Col, Value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableEdit.OnDirty");
        }
    };
}

/** The add-row: leaving a filled new row hands it to the run once; a refusal or a missing cell leaves it as typed. */
export class AppMoneyTableAdd {
    public Grid: kEditableTable;
    private Host: HTMLElement;
    private Count: number;
    private Adder: IAppMoneyAdder;
    private LastRow: number = -1;
    private Leaving: number = -1;
    /** The page-made id per new row, kept until a repaint, so a retry of the same row lands once. */
    private Ids: Record<number, string> = {};

    public constructor(Grid: kEditableTable, Host: HTMLElement, Count: number, Adder: IAppMoneyAdder) {
        this.Grid = Grid;
        this.Host = Host;
        this.Count = Count;
        this.Adder = Adder;
    }

    public OnSelect = (Cell: kEditableTableCell | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Cell === null || Cell === undefined) { return; }
            this.AtRow(Cell.RowIndex);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.OnSelect");
        }
    };

    /** A press, a click or a key inside the table: the row the grid's current cell (or the event's row) is on now. */
    public OnTouch = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            let Row: number = -1;
            const Tr: Element | null = (Ev.target as Element).closest("tr");
            for (let I: number = 0; I < this.Grid.Rows.Items.length; I++) { if (this.Grid.Rows.Items[I].Row === Tr) { Row = I; } }
            if (Ev.type === "keyup" && this.Grid.CurrenEditableCell !== null) { Row = this.Grid.CurrenEditableCell.RowIndex; }
            if (Row >= 0) { this.AtRow(Row); }
            window.setTimeout(this.NameFields, 0);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.OnTouch");
        }
    };

    /** The grid's in-cell editors (a choice list, a date) are named by their column's header, so each has a label. */
    private NameFields = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            // The field's cell -> its grid cell -> that column's heading (`Grid.Headers`, one per column).
            for (const Field of Array.from(this.Host.querySelectorAll<HTMLElement>("td select, td input"))) {
                if (Field.hasAttribute("aria-label") === true) { continue; }
                const Td: HTMLTableCellElement | null = Field.closest("td");
                for (const Row of this.Grid.Rows.Items) {
                    for (const Cell of Row.Cells) {
                        if (Cell.Cell === Td) { Field.setAttribute("aria-label", this.HeadingOf(Cell.ColumnIndex)); }
                    }
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.NameFields");
        }
    };

    /** A column's heading words ("" when the grid has none for it). DOCUMENTED EXCEPTION: pure lookup. */
    private HeadingOf = (Column: number): string => {
        if (Column < 0 || Column >= this.Grid.Headers.length) { return ""; }
        const Cell: HTMLTableCellElement | null = this.Grid.Headers[Column].HeaderCell;
        if (Cell === null) { return ""; }
        return nz(Cell.textContent).trim();
    };

    /** Moving to another row commits the one left. */
    private AtRow = (Row: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Row === this.LastRow) { return; }
            const Left: number = this.LastRow;
            this.LastRow = Row;
            this.Commit(Left);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.AtRow");
        }
    };

    /**
     * A press anywhere outside the table commits the row it was on. ⚠ Not `focusout`: the grid moves focus off a
     * choice list on Tab while the cursor stays in the row, which read as leaving it. A repaint retires the listener.
     */
    public OnLeave = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Host.isConnected === false) { document.removeEventListener("mousedown", this.OnLeave, true); return; }
            const To: Node | null = Ev.target as Node | null;
            if (To !== null && this.Host.contains(To) === true) { return; }
            if (this.LastRow < 0) { return; }
            this.Leaving = this.LastRow;
            this.LastRow = -1;
            // The cell being typed in commits its value on the same blur, AFTER this listener - read the row a turn later.
            window.setTimeout(this.OnLeft, 0);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.OnLeave");
        }
    };

    private OnLeft = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Left: number = this.Leaving;
            this.Leaving = -1;
            this.Commit(Left);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.OnLeft");
        }
    };

    private Commit = (RowIndex: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (RowIndex < this.Count) { return; }
            const Open: kEditableTableCell | null = this.Grid.CurrenEditableCell;
            // A cell still being typed in holds its words only in the page: take them without ending the edit.
            if (Open !== null && Open.RowIndex === RowIndex && Open.Cell.contentEditable === "true") { Open.SyncInlineValueForHarvest(); }
            const Row: kEditableTableRow | undefined = this.Grid.Rows.Items[RowIndex];
            if (Row === undefined || Row.IsEmpty() === true) { this.Mark(RowIndex, []); return; }
            const Cells: Record<string, string> = {};
            for (const Cell of Row.Cells) {
                const Col: string = nz(Cell.ColDef.KObjectPropertyName);
                if (Col.length > 0) { Cells[Col] = nz(Row.GetValueFromCell(Col)); }
            }
            if (this.Ids[RowIndex] === undefined) { this.Ids[RowIndex] = this.Adder.NewId(); }
            this.Mark(RowIndex, this.Adder.AddRow(this.Ids[RowIndex], Cells));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.Commit");
        }
    };

    /** The cells to fill, outlined and `aria-invalid` (validation, in the row); every other cell cleared. */
    private Mark = (RowIndex: number, Missing: string[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Row: kEditableTableRow | undefined = this.Grid.Rows.Items[RowIndex];
            if (Row === undefined) { return; }
            for (const Cell of Row.Cells) {
                const Bad: boolean = Missing.indexOf(nz(Cell.ColDef.KObjectPropertyName)) >= 0;
                Cell.Cell.classList.toggle("minvalid", Bad);
                if (Bad === true) { Cell.Cell.setAttribute("aria-invalid", "true"); } else { Cell.Cell.removeAttribute("aria-invalid"); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableAdd.Mark");
        }
    };
}

/** The `Open` column's painter: a real link to the item's message, named for screen readers, nothing on the add-row. */
export class AppMoneyTableLink {
    private Rows: AppMoneyEntry[];

    public constructor(Rows: AppMoneyEntry[]) {
        this.Rows = Rows;
    }

    public Paint = (Value: unknown, Cell: kEditableTableCell): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Cell.Cell.textContent = "";
            const At: string = nz(Value as string);
            const E: AppMoneyEntry | undefined = this.Rows[Cell.RowIndex];
            if (At.length === 0 || E === undefined) { return; }
            const A: HTMLAnchorElement = document.createElement("a");
            A.href = At;
            A.className = "mopen";
            // ⭐ AN ICON, NOT A REPEATED WORD (owner review 2026-11-01: "View" on every row); the name is the aria-label + title.
            A.innerHTML = AppMoneyModel.OpenIcon();
            A.setAttribute("aria-label", AppMoneyModel.OpenName(E.Label));
            A.title = AppMoneyModel.OpenName(E.Label);
            // A link reached by Tab selects its own cell, so the grid's "Enter on a cell whose only control is a link
            // clicks it" applies (the grid answers Enter for the whole table, so a bare focused link would not move).
            A.addEventListener("focus", new AppMoneyTableLinkFocus(Cell).OnFocus);
            Cell.Cell.appendChild(A);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableLink.Paint");
        }
    };
}

/** One link's focus: select the cell that holds it. */
export class AppMoneyTableLinkFocus {
    private Cell: kEditableTableCell;

    public constructor(Cell: kEditableTableCell) {
        this.Cell = Cell;
    }

    public OnFocus = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Cell.ParentTable !== null && this.Cell.ParentTable.CurrenEditableCell !== this.Cell) { this.Cell.Select(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyTableLinkFocus.OnFocus");
        }
    };
}
